import frameSvgTemplate from './assets/frame.svg?raw';
import { getLogger } from './logger';
import type { WidgetDisclaimer, WidgetPosition, WidgetTranslations } from './types';

const logger = getLogger('UI');

/**
 * Check browser SVG support
 */
export function checkSvgSupport(): boolean {
  return !!(
    typeof SVGRect !== 'undefined' &&
    document.createElementNS &&
    document.createElementNS('http://www.w3.org/2000/svg', 'svg').createSVGRect
  );
}

/**
 * Escape special characters to prevent SVG injection
 */
function escapeSvgText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Shared text styling for both disclaimer lines */
const DISCLAIMER_FONT =
  `font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, system-ui, sans-serif" ` +
  `font-size="26" font-weight="400" fill="#6B7280"`;

/**
 * Line one sits under the state status text ("Streaming audio", "Connecting", …),
 * which the visualizations render centered at (580, 465).
 */
const DISCLAIMER_LINE_ONE_X = 580;
const DISCLAIMER_LINE_ONE_Y = 510;

/** Line two sits in the white footer band (627..751), left of the stop button */
const DISCLAIMER_LINE_TWO_ICON_X = 50;
const DISCLAIMER_LINE_TWO_ICON_Y = 669;
const DISCLAIMER_LINE_TWO_TEXT_X = 106;
const DISCLAIMER_LINE_TWO_BASELINE = 698;

/**
 * Info icon (40x40) shown to the left of disclaimer line two.
 */
const DISCLAIMER_ICON = `<circle cx="20" cy="20" r="18" stroke="#3D4CFF" stroke-width="2.5"/>
<circle cx="20" cy="12" r="2" fill="#3D4CFF"/>
<path d="M20 18V29" stroke="#3D4CFF" stroke-width="2.5" stroke-linecap="round"/>`;

/**
 * Build the SVG markup for disclaimer line one, centered under the status text.
 * Returns an empty string when the line is not configured.
 */
function renderDisclaimerLineOne(line?: string): string {
  const text = (line ?? '').trim();
  if (!text) {
    return '';
  }

  return (
    `<g id="disclaimer-line-one" class="sirmaai-disclaimer" aria-hidden="true">` +
    `<text x="${DISCLAIMER_LINE_ONE_X}" y="${DISCLAIMER_LINE_ONE_Y}" text-anchor="middle" ${DISCLAIMER_FONT}>${escapeSvgText(text)}</text>` +
    `</g>`
  );
}

/**
 * Build the SVG markup for disclaimer line two: an info icon followed by the
 * text, on the left of the footer. Returns an empty string when not configured.
 */
function renderDisclaimerLineTwo(line?: string): string {
  const text = (line ?? '').trim();
  if (!text) {
    return '';
  }

  return (
    `<g id="disclaimer-line-two" class="sirmaai-disclaimer" aria-hidden="true">` +
    `<g transform="translate(${DISCLAIMER_LINE_TWO_ICON_X}, ${DISCLAIMER_LINE_TWO_ICON_Y})" fill="none">${DISCLAIMER_ICON}</g>` +
    `<text x="${DISCLAIMER_LINE_TWO_TEXT_X}" y="${DISCLAIMER_LINE_TWO_BASELINE}" ${DISCLAIMER_FONT}>${escapeSvgText(text)}</text>` +
    `</g>`
  );
}

/**
 * Process SVG template and replace placeholders with translations
 */
export function processSvgTemplate(
  translations: WidgetTranslations,
  disclaimer: WidgetDisclaimer = getDisclaimerLines()
): string {
  let svgContent = frameSvgTemplate;

  // Replace placeholders with translation values
  svgContent = svgContent.replace(/{{title}}/g, escapeSvgText(translations.assistantName));
  svgContent = svgContent.replace(
    /{{description}}/g,
    escapeSvgText(translations.description || '')
  );
  svgContent = svgContent.replace(
    /{{disclaimerLineOne}}/g,
    renderDisclaimerLineOne(disclaimer.lineOne)
  );
  svgContent = svgContent.replace(
    /{{disclaimerLineTwo}}/g,
    renderDisclaimerLineTwo(disclaimer.lineTwo)
  );

  // Add browser-specific attributes if needed
  const isIE = /MSIE|Trident/.test(navigator.userAgent);
  if (isIE) {
    // Add IE-specific SVG fixes
    svgContent = svgContent.replace('<svg ', '<svg role="img" ');
  }

  return svgContent;
}

/**
 * Default translations for the widget
 */
export const defaultTranslations: WidgetTranslations = {
  assistantName: 'AI Assistant',
  connecting: 'Connecting',
  error: 'An error occurred',
  streaming: 'Listening',
  stop: 'Stop',
  ended: 'Conversation ended',
  description: 'Hello! I am your AI assistant. How can I help you today?',
};

/**
 * Get translations from window.SirmaAI.translations with type safety
 * Returns default translations merged with any provided translations
 */
export function getTranslations(): WidgetTranslations {
  logger.debug('Getting translations');
  const customTranslations = window?.SirmaAI?.translations;

  if (!customTranslations) {
    logger.debug('No custom translations found, using defaults');
    return defaultTranslations;
  }

  // Merge custom translations over defaults
  const mergedTranslations = { ...defaultTranslations, ...customTranslations };
  const customKeys = Object.keys(customTranslations);

  logger.debug('Merged translations', {
    customKeys,
    hasCustomTranslations: customKeys.length > 0,
  });

  return mergedTranslations;
}

/**
 * Get the optional disclaimer lines from window.SirmaAI.config.
 * Both lines are optional; missing/blank values are simply not rendered.
 */
export function getDisclaimerLines(): WidgetDisclaimer {
  const config = window?.SirmaAI?.config;
  const disclaimer: WidgetDisclaimer = {
    lineOne: config?.disclaimerLineOne,
    lineTwo: config?.disclaimerLineTwo,
  };

  logger.debug('Getting disclaimer lines', {
    hasLineOne: !!disclaimer.lineOne,
    hasLineTwo: !!disclaimer.lineTwo,
  });

  return disclaimer;
}

/**
 * Get widget position from window.SirmaAI.config.position with type safety
 * Returns 'center' as default position when not specified or invalid
 */
export function getWidgetPosition(): WidgetPosition {
  logger.debug('Getting widget position');
  const configuredPosition = window?.SirmaAI?.config?.position;

  if (!configuredPosition) {
    logger.debug('No position configured, using default: center');
    return 'center';
  }

  // Validate that the configured position is one of the allowed values
  const validPositions: WidgetPosition[] = [
    'top-left',
    'top-center',
    'top-right',
    'center-left',
    'center',
    'center-right',
    'bottom-left',
    'bottom-center',
    'bottom-right',
  ];

  if (validPositions.includes(configuredPosition)) {
    logger.debug('Using configured position', { position: configuredPosition });
    return configuredPosition;
  } else {
    logger.warn('Invalid position configured, using default: center', {
      invalidPosition: configuredPosition,
      validPositions,
    });
    return 'center';
  }
}
